import { z } from 'zod';

/**
 * **Os planetas de mineração: o segundo recurso do mundo aberto.**
 *
 * ## Em que ele difere de um cometa
 *
 * A anatomia é a mesma — um corpo imóvel feito de partes que se partem uma a
 * uma — e é por isso que a fundação é partilhada. O que difere é **onde a
 * recompensa vive**:
 *
 * | | cometa | planeta |
 * | --- | --- | --- |
 * | ouro | proporcional a cada golpe | dentro de partes, tudo ou nada |
 * | item | uma joia, atrás de um núcleo | **um a três** itens, um por núcleo |
 * | quem leva | o último golpe no núcleo | o último golpe em cada coisa |
 * | reconstrução | trinta minutos | trinta minutos |
 * | geração nova | uma hora | **trinta minutos** |
 *
 * O ouro de um planeta não é proporcional: uma parte tem ouro ou não tem, e
 * quem a parte leva o que ela tinha. É uma aposta em vez de um salário, e é
 * essa a diferença que faz partir um planeta ser outra coisa de partir um
 * cometa.
 *
 * ## O segredo, e quanto dele existe
 *
 * Que partes têm ouro, quanto, quantos núcleos há, e que item está em cada um:
 * tudo isso é selado com entropia secreta do mundo e **nunca atravessa o
 * protocolo**. A quantidade de núcleos só se torna pública à medida que eles
 * ficam expostos — antes disso, um planeta com um núcleo e um planeta com três
 * têm exatamente a mesma forma na trama.
 *
 * O ouro de uma parte só se revela **no instante em que ela se parte**. Antes
 * disso não há campo onde a diferença coubesse, o que é o que impede um cliente
 * de escolher por onde começar.
 */

/* -------------------------------------------------------------------------- */
/*  Os relógios                                                               */
/* -------------------------------------------------------------------------- */

/**
 * **Trinta minutos sem dano, e o corpo volta.**
 *
 * O mesmo prazo do cometa, e a mesma pergunta — por isso a fundação responde
 * às duas. Tempo de RELÓGIO: um quadrante vazio não corre tiques.
 */
export const OPEN_WORLD_PLANET_REBUILD_IDLE_MS = 30 * 60 * 1_000;

/**
 * **Trinta minutos depois de ESGOTADO, e nasce outra geração.**
 *
 * Metade do que um cometa espera, e é deliberado: um planeta é mais trabalho
 * para abrir, e faze-lo esperar o dobro tornaria o segundo recurso mais raro do
 * que o primeiro sem que ninguém o tivesse decidido.
 *
 * "Esgotado" tem uma definição estrita, e é ela que separa este relógio do de
 * cima: todas as partes destruídas, **todos os núcleos obtidos**, e nenhuma
 * recompensa por pagar. Um corpo aberto com um núcleo por levar não está
 * esgotado, e não renasce — o núcleo fica exposto até alguém ter espaço.
 */
export const OPEN_WORLD_PLANET_RESPAWN_MS = 30 * 60 * 1_000;

/** Quantos núcleos um planeta tem, inclusive. Do contrato aprovado. */
export const OPEN_WORLD_PLANET_CORES_MIN = 1;
export const OPEN_WORLD_PLANET_CORES_MAX = 3;

/* -------------------------------------------------------------------------- */
/*  A identidade das recompensas                                              */
/* -------------------------------------------------------------------------- */

const SEPARATOR = '|';

/**
 * **O id do ouro de UMA parte**, derivado e nunca cunhado.
 *
 * Mundo, planeta, geração e o índice da parte. A conta não entra: a chave nomeia
 * a PARTE. Mas o livro-razão é único por conta e chave, e por isso a chave
 * sozinha não impede duas contas de receberem o mesmo ouro — quem o impede é a
 * sala, que grava o dono na placa no golpe que a parte e pede sempre para essa
 * conta (gate V28I.BZ).
 *
 * Com o dono fixo, repetir é grátis: uma falha volta a tentar a mesma operação,
 * uma resposta perdida lê a que já existe, e uma reconstrução não repõe o ouro —
 * a parte volta vazia porque o selo já a marcou como paga.
 */
export function openWorldPlanetGoldEventId(input: {
  readonly worldId: string;
  readonly planetId: string;
  readonly generation: number;
  readonly partIndex: number;
}): string {
  return [
    'planet-gold',
    input.worldId,
    input.planetId,
    String(input.generation),
    String(input.partIndex),
  ].join(SEPARATOR);
}

/**
 * **O id do item de UM núcleo.** A conta não participa.
 *
 * "Trocar o atacante não pode criar outro item para o mesmo núcleo" é
 * exatamente isto: a chave nomeia o núcleo daquela geração, e duas tentativas
 * — do mesmo jogador ou de dois diferentes — são a mesma operação para a
 * autoridade económica.
 */
export function openWorldPlanetItemEventId(input: {
  readonly worldId: string;
  readonly planetId: string;
  readonly generation: number;
  readonly coreIndex: number;
}): string {
  return [
    'planet-item',
    input.worldId,
    input.planetId,
    String(input.generation),
    String(input.coreIndex),
  ].join(SEPARATOR);
}

/* -------------------------------------------------------------------------- */
/*  A forma pública                                                           */
/* -------------------------------------------------------------------------- */

/** O id público de um planeta: o id do espaço que ele ocupa. */
export const openWorldPlanetIdSchema = z.string().min(1).max(64);

/**
 * **Um planeta a ENTRAR na vista**: o que não muda dentro de uma geração.
 *
 * Note o que NÃO está aqui: quantos núcleos existem. Um planeta com um e um
 * planeta com três entram exatamente da mesma maneira, e a diferença só se
 * torna pública quando um núcleo fica exposto.
 */
export const openWorldPlanetEntrySchema = z
  .object({
    i: openWorldPlanetIdSchema,
    g: z.number().int().min(1),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** O raio do corpo, em milli-unidades. Maior do que o de um cometa. */
    r: z.number().int().min(1),
    /** Quantas placas esta geração teve ao nascer. */
    p: z.number().int().min(1).max(32),
    /** A semente VISUAL, pública. Decide silhuetas e nada de valor. */
    v: z.number().int().min(0),
  })
  .strict();

export type OpenWorldPlanetEntry = z.infer<typeof openWorldPlanetEntrySchema>;

/**
 * Um núcleo **exposto**, e só um exposto aparece aqui.
 *
 * O índice viaja porque múltiplos núcleos têm de ser distinguíveis no ecrã: o
 * cliente desenha-os em sítios diferentes, e sem o índice dois núcleos do mesmo
 * planeta seriam a mesma coisa desenhada duas vezes.
 */
export const openWorldPlanetCoreSchema = z
  .object({
    /** Qual núcleo, 0..2. Estável dentro da geração. */
    c: z.number().int().min(0).max(2),
    /** A vida pública, em milésimos do total. */
    h: z.number().int().min(0).max(1_000),
  })
  .strict();

export type OpenWorldPlanetCore = z.infer<typeof openWorldPlanetCoreSchema>;

/**
 * **O estado de um planeta**, nas tramas em que ele mudou.
 *
 * A máscara das placas, e os núcleos **expostos**. Um núcleo que ainda está
 * coberto não tem entrada nesta lista, portanto o cliente não sabe que ele
 * existe — e não sabe quantos faltam.
 */
export const openWorldPlanetStateSchema = z
  .object({
    i: openWorldPlanetIdSchema,
    /** As placas ainda presentes. O bit `n` é a placa `n`. */
    m: z.number().int().min(0),
    /** Os núcleos EXPOSTOS. Vazio enquanto o corpo os cobrir todos. */
    c: z.array(openWorldPlanetCoreSchema).max(3),
  })
  .strict();

export type OpenWorldPlanetState = z.infer<typeof openWorldPlanetStateSchema>;

/** O que aconteceu a um planeta, para o cliente desenhar uma vez e esquecer. */
export const OPEN_WORLD_PLANET_EVENTS = [
  /** Um golpe aterrou numa placa ou num núcleo. */
  'impact',
  /** Uma placa partiu-se. */
  'fragment',
  /**
   * Uma placa partida tinha ouro, e ele foi creditado.
   *
   * O único momento em que o ouro de uma parte se torna público — e é depois de
   * ela já não existir, portanto não diz a ninguém por onde começar.
   */
  'gold',
  /** Um núcleo ficou exposto. */
  'reveal',
  /** Um item foi levado. */
  'acquired',
  /** A carga estava cheia: o núcleo fica, o item fica, ninguém o levou. */
  'full',
  /** O corpo reconstruiu-se depois de meia hora de sossego. */
  'rebuilt',
] as const;

export type OpenWorldPlanetEventKind = (typeof OPEN_WORLD_PLANET_EVENTS)[number];

/** Um evento, deduplicável pelo ordinal da sua geração. */
export const openWorldPlanetEventSchema = z
  .object({
    i: openWorldPlanetIdSchema,
    /** O ordinal dentro desta geração. Estritamente crescente. */
    n: z.number().int().min(1),
    k: z.enum(OPEN_WORLD_PLANET_EVENTS),
    /** A placa, ou `-1` quando o evento é do corpo ou de um núcleo. */
    p: z.number().int().min(-1).max(31),
    /** O núcleo, ou `-1` quando o evento não é de um núcleo. */
    c: z.number().int().min(-1).max(2),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
  })
  .strict();

export type OpenWorldPlanetEvent = z.infer<typeof openWorldPlanetEventSchema>;

/** Um planeta que saiu da vista, ou que deixou de existir. */
export const openWorldPlanetGoneSchema = openWorldPlanetIdSchema;
